import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/team/invite/lookup`, { params: { token } })
      .then(r => setInvite(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Invalid invitation"))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/team/invite/accept`, { token, name, password });
      localStorage.setItem("bf_token", r.data.token);
      await checkAuth();
      toast.success("Welcome to the team!");
      nav("/dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#070B14] flex items-center justify-center"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  if (error || !invite) return (
    <div className="min-h-screen bg-[#070B14] text-white flex items-center justify-center p-8">
      <div className="max-w-md w-full bg-[#0D1424] border border-white/10 p-8 text-center">
        <div className="mono-label text-red-400 mb-3">// INVITATION INVALID</div>
        <p className="text-slate-400 mb-6">{error || "This invitation link is invalid or has expired."}</p>
        <Link to="/" className="mono-label text-cyan-400 hover:text-cyan-300">BACK TO HOME</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#070B14] text-white flex items-center justify-center p-8" data-testid="accept-invite-page">
      <div className="max-w-md w-full bg-[#0D1424] border border-cyan-400/40 p-8">
        <div className="flex items-center gap-2 mb-6">
          <Logo className="w-9 h-9" />
          <div className="font-display font-black text-lg">BIDFORGE</div>
        </div>
        <div className="mono-label text-cyan-400 mb-3">// WORKSPACE INVITATION</div>
        <h1 className="font-display font-black text-3xl mb-2 tracking-tight">Join {invite.workspace_owner_name || "the team"}</h1>
        <p className="text-slate-400 mb-6 text-sm">You're invited by <span className="text-white">{invite.invited_by_name}</span> to join their BidForge workspace as <span className="text-cyan-400 mono-label">{invite.role.toUpperCase()}</span>.</p>

        <form onSubmit={accept} className="space-y-4">
          <div>
            <div className="mono-label text-slate-400 mb-2">Email</div>
            <input value={invite.email} disabled className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-slate-400 outline-none" data-testid="invite-email-display" />
          </div>
          <div>
            <div className="mono-label text-slate-400 mb-2">Your name</div>
            <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2.5 text-sm outline-none" data-testid="accept-name" />
          </div>
          <div>
            <div className="mono-label text-slate-400 mb-2">Set password</div>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2.5 text-sm outline-none" data-testid="accept-password" />
          </div>
          <button type="submit" disabled={submitting} className="w-full bg-cyan-400 text-[#070B14] py-3 font-display font-bold text-sm uppercase tracking-wider hover:bg-cyan-300 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="accept-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />JOINING</> : <>ACCEPT AND JOIN <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
